import { useCallback, useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, Circle, Polygon, useMapEvents, useMap, Marker, Popup } from "react-leaflet";
import { MapPin, Circle as CircleIcon, Polygon as PolygonIcon, MagnifyingGlass, X } from "@phosphor-icons/react";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

// Fix Leaflet's default marker icon URLs (webpack/CRA can't resolve them via
// CSS). Self-hosted from `/public/leaflet/` so we don't depend on the unpkg
// CDN (removes SRI + availability + referer-leak concerns).
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "/leaflet/marker-icon-2x.png",
  iconUrl: "/leaflet/marker-icon.png",
  shadowUrl: "/leaflet/marker-shadow.png",
});

// Sensible starting view — centered on Australia so the default flow lands
// somewhere useful for AU-wide campaigns.
const DEFAULT_CENTER = [-25.2744, 133.7751];
const DEFAULT_ZOOM = 4;

/**
 * Interactive OpenStreetMap picker for geo targeting. Two draw modes:
 *  - `radius`: click anywhere to drop a circle of the configured km radius.
 *    Click an existing marker to remove it.
 *  - `polygon`: click to add points; double-click (or hit "finish polygon") to
 *    commit the shape. In-progress polygons are shown dashed.
 *
 * Emits `onChange({ radii, polygons })` — the parent GeoTargetingDialog just
 * merges these into its GeoRule payload alongside countries/states/cities.
 */
function ClickHandler({ mode, radiusKm, onRadius, drafting, setDrafting }) {
  useMapEvents({
    click(e) {
      const { lat, lng } = e.latlng;
      if (mode === "radius") {
        onRadius({ lat, lon: lng, km: radiusKm, label: `${lat.toFixed(3)},${lng.toFixed(3)}` });
      } else if (mode === "polygon") {
        setDrafting([...drafting, [lat, lng]]);
      }
    },
    dblclick() {
      // dblclick handled by parent via "Finish polygon" button; leave native
      // zoom off (see MapContainer doubleClickZoom={false}).
    },
  });
  return null;
}

/**
 * Small hook-holder that gives the parent an imperative `flyTo` handle by
 * writing the Leaflet map instance to `mapHandleRef.current`.
 */
function MapHandle({ mapHandleRef }) {
  const map = useMap();
  useEffect(() => {
    mapHandleRef.current = map;
  }, [map, mapHandleRef]);
  return null;
}

/**
 * Free-text search box that hits OpenStreetMap Nominatim. No API key needed
 * (Nominatim's public tier — usage policy: ≤1 req/s, max 5 results, must
 * identify UA which browsers set to the User-Agent). We debounce by 400ms
 * to stay well within the policy and only fire on ≥3 chars.
 *
 * Restricted to Australia by default (`countrycodes=au`) — matches the
 * app's default geo target. Toggle the globe icon to search worldwide.
 */
function SearchBox({ mapHandleRef, radiusKm, mode, onRadius }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [auOnly, setAuOnly] = useState(true);
  const abortRef = useRef(null);

  useEffect(() => {
    if (q.trim().length < 3) {
      setResults([]);
      return;
    }
    const t = setTimeout(async () => {
      // Cancel any in-flight lookup
      if (abortRef.current) abortRef.current.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      setLoading(true);
      try {
        const params = new URLSearchParams({
          q: q.trim(),
          format: "json",
          limit: "5",
          addressdetails: "1",
        });
        if (auOnly) params.set("countrycodes", "au");
        const resp = await fetch(`https://nominatim.openstreetmap.org/search?${params}`, {
          signal: ctrl.signal,
          headers: { Accept: "application/json" },
        });
        if (!resp.ok) throw new Error("search failed");
        const data = await resp.json();
        setResults(data);
      } catch (e) {
        if (e.name !== "AbortError") setResults([]);
      } finally {
        setLoading(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [q, auOnly]);

  const flyTo = useCallback((lat, lon, displayName) => {
    if (mapHandleRef.current) {
      mapHandleRef.current.flyTo([lat, lon], 12, { duration: 1.2 });
    }
    // Also drop a radius immediately when in radius mode — saves the user a click.
    if (mode === "radius") {
      onRadius({
        lat,
        lon,
        km: radiusKm,
        label: displayName.slice(0, 60),
      });
    }
    setResults([]);
    setQ(displayName.split(",")[0]);
  }, [mapHandleRef, mode, radiusKm, onRadius]);

  const clear = () => {
    setQ("");
    setResults([]);
  };

  return (
    <div className="relative">
      <div className="flex items-center gap-1 border border-white/10 bg-[#0a0a0a]">
        <MagnifyingGlass size={13} className="text-zinc-500 ml-2" />
        <input
          data-testid="map-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={auOnly ? "search AU · postcode, suburb, address (e.g. 3000 or Bondi)" : "search worldwide"}
          className="flex-1 bg-transparent border-0 px-2 py-2 text-sm text-zinc-100 focus:outline-none placeholder:text-zinc-600"
        />
        {q && (
          <button
            data-testid="map-search-clear"
            onClick={clear}
            className="text-zinc-500 hover:text-white p-1"
            title="clear"
          >
            <X size={12} />
          </button>
        )}
        <button
          data-testid="map-search-au-toggle"
          onClick={() => setAuOnly((v) => !v)}
          className={`px-2 py-1 font-mono text-[10px] uppercase transition-colors border-l border-white/10 ${
            auOnly ? "text-[#ccff00]" : "text-zinc-500 hover:text-white"
          }`}
          title={auOnly ? "AU-only search · click to expand worldwide" : "Worldwide · click to restrict to AU"}
        >
          {auOnly ? "🇦🇺 AU" : "🌐 WORLD"}
        </button>
      </div>
      {loading && <div className="absolute right-16 top-2.5 text-[10px] text-zinc-500 font-mono">searching…</div>}
      {results.length > 0 && (
        <ul
          data-testid="map-search-results"
          className="absolute z-[1000] left-0 right-0 top-full mt-1 bg-[#0a0a0a] border border-white/10 max-h-56 overflow-y-auto"
        >
          {results.map((r) => (
            <li key={r.place_id}>
              <button
                data-testid={`map-search-result-${r.place_id}`}
                onClick={() => flyTo(parseFloat(r.lat), parseFloat(r.lon), r.display_name)}
                className="w-full text-left px-3 py-2 hover:bg-[#121212] hover:text-[#ccff00] text-xs text-zinc-300 transition-colors flex items-start gap-2"
              >
                <MapPin size={12} className="mt-0.5 text-zinc-500 shrink-0" />
                <span className="truncate">{r.display_name}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function GeoMapPicker({ radii, polygons, onChange }) {
  const [mode, setMode] = useState("radius");
  const [radiusKm, setRadiusKm] = useState(50);
  const [drafting, setDrafting] = useState([]); // in-progress polygon points
  const mapRef = useRef(null);
  // Handle to the underlying Leaflet map — populated by <MapHandle/> so
  // SearchBox can call `flyTo` when the user picks a postcode/address result.
  const mapHandleRef = useRef(null);

  const addRadius = (r) => onChange({ radii: [...radii, r], polygons });

  const removeRadius = (i) => {
    const next = radii.filter((_, idx) => idx !== i);
    onChange({ radii: next, polygons });
  };

  const finishPolygon = () => {
    if (drafting.length < 3) {
      setDrafting([]);
      return;
    }
    onChange({ radii, polygons: [...polygons, drafting] });
    setDrafting([]);
  };

  const removePolygon = (i) => {
    const next = polygons.filter((_, idx) => idx !== i);
    onChange({ radii, polygons: next });
  };

  const cancelDraft = () => setDrafting([]);

  return (
    <div className="space-y-2" data-testid="geo-map-picker">
      {/* Search — postcode, suburb, address; flies the map + optionally
          drops a radius (radius mode only) */}
      <SearchBox
        mapHandleRef={mapHandleRef}
        radiusKm={radiusKm}
        mode={mode}
        onRadius={addRadius}
      />

      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex border border-white/10">
          <button
            data-testid="map-mode-radius"
            onClick={() => { setMode("radius"); setDrafting([]); }}
            className={`flex items-center gap-1 px-3 py-1.5 font-mono text-[10px] uppercase transition-colors ${
              mode === "radius" ? "bg-[#ccff00] text-black" : "bg-[#0a0a0a] text-zinc-400 hover:text-white"
            }`}
          >
            <CircleIcon size={12} weight="bold" />
            radius
          </button>
          <button
            data-testid="map-mode-polygon"
            onClick={() => setMode("polygon")}
            className={`flex items-center gap-1 px-3 py-1.5 font-mono text-[10px] uppercase border-l border-white/10 transition-colors ${
              mode === "polygon" ? "bg-[#ccff00] text-black" : "bg-[#0a0a0a] text-zinc-400 hover:text-white"
            }`}
          >
            <PolygonIcon size={12} weight="bold" />
            polygon
          </button>
        </div>

        {mode === "radius" && (
          <label className="flex items-center gap-1 font-mono text-[10px] uppercase text-zinc-400">
            radius
            <input
              type="number"
              min="1"
              max="1000"
              value={radiusKm}
              onChange={(e) => setRadiusKm(Math.max(1, Number(e.target.value) || 50))}
              className="w-16 bg-[#0a0a0a] border border-white/10 px-2 py-1 text-sm text-zinc-200 focus:border-[#ccff00] focus:outline-none"
              data-testid="map-radius-km"
            />
            km
          </label>
        )}

        {mode === "polygon" && drafting.length > 0 && (
          <>
            <span className="font-mono text-[10px] text-zinc-500">
              {drafting.length} point{drafting.length !== 1 ? "s" : ""} · click to add
            </span>
            <button
              data-testid="map-finish-polygon"
              onClick={finishPolygon}
              disabled={drafting.length < 3}
              className="flex items-center gap-1 px-2 py-1 bg-[#ccff00] text-black hover:bg-[#b3e600] disabled:opacity-40 font-mono text-[10px] font-bold uppercase"
            >
              finish ({drafting.length}≥3)
            </button>
            <button
              data-testid="map-cancel-polygon"
              onClick={cancelDraft}
              className="px-2 py-1 bg-[#121212] border border-white/10 text-zinc-400 hover:text-white font-mono text-[10px] uppercase"
            >
              cancel
            </button>
          </>
        )}

        <div className="ml-auto font-mono text-[10px] text-zinc-500">
          {radii.length} radius · {polygons.length} polygon
        </div>
      </div>

      {/* The map */}
      <div className="border border-white/10" style={{ height: 380 }}>
        <MapContainer
          center={DEFAULT_CENTER}
          zoom={DEFAULT_ZOOM}
          doubleClickZoom={false}
          scrollWheelZoom
          style={{ height: "100%", width: "100%", background: "#0a0a0a" }}
          whenCreated={(m) => (mapRef.current = m)}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapHandle mapHandleRef={mapHandleRef} />
          <ClickHandler
            mode={mode}
            radiusKm={radiusKm}
            onRadius={addRadius}
            drafting={drafting}
            setDrafting={setDrafting}
          />
          {/* Committed radii */}
          {radii.map((r, i) => (
            <Circle
              key={`r-${i}`}
              center={[r.lat, r.lon]}
              radius={(r.km || 50) * 1000}
              pathOptions={{ color: "#ccff00", fillColor: "#ccff00", fillOpacity: 0.15, weight: 2 }}
            >
              <Popup>
                <div style={{ fontFamily: "monospace", fontSize: 11 }}>
                  {r.label || `${r.lat.toFixed(3)},${r.lon.toFixed(3)}`}<br />
                  {r.km} km
                  <br />
                  <button
                    onClick={() => removeRadius(i)}
                    style={{ marginTop: 4, background: "#ef4444", color: "white", padding: "2px 6px", border: 0, cursor: "pointer" }}
                  >
                    remove
                  </button>
                </div>
              </Popup>
            </Circle>
          ))}
          {/* Committed polygons */}
          {polygons.map((poly, i) => (
            <Polygon
              key={`p-${i}`}
              positions={poly}
              pathOptions={{ color: "#ccff00", fillColor: "#ccff00", fillOpacity: 0.15, weight: 2 }}
            >
              <Popup>
                <div style={{ fontFamily: "monospace", fontSize: 11 }}>
                  polygon · {poly.length} points
                  <br />
                  <button
                    onClick={() => removePolygon(i)}
                    style={{ marginTop: 4, background: "#ef4444", color: "white", padding: "2px 6px", border: 0, cursor: "pointer" }}
                  >
                    remove
                  </button>
                </div>
              </Popup>
            </Polygon>
          ))}
          {/* In-progress polygon: dashed outline + markers on each point */}
          {drafting.length > 0 && (
            <>
              <Polygon
                positions={drafting}
                pathOptions={{ color: "#f59e0b", dashArray: "6 4", fillOpacity: 0.05, weight: 2 }}
              />
              {drafting.map((pt, i) => (
                <Marker key={`d-${i}`} position={pt}>
                  <Popup>point #{i + 1}</Popup>
                </Marker>
              ))}
            </>
          )}
        </MapContainer>
      </div>

      <div className="font-mono text-[10px] text-zinc-500 flex items-center gap-1">
        <MapPin size={10} />
        {mode === "radius"
          ? "click anywhere to add a circle · click an existing circle to remove"
          : "click to add points · finish when you have ≥3"}
      </div>
    </div>
  );
}
